import { z } from 'zod';

/**
 * @aiPurpose wrap a Patch input schema so an empty body emits
 *   `'patch must contain at least one field'` ONLY when the raw input had
 *   zero own keys to begin with — not when the keys were unknown and got
 *   stripped by the inner `.strict()`.
 * @aiInvariant the inner schema runs only on non-empty raw objects; on
 *   empty `{}` the issue is added with `fatal: true` and `z.NEVER` aborts
 *   parsing before the inner schema runs, so the only issue surfaced is
 *   the `custom: 'patch must contain at least one field'` message
 *   (without `fatal: true`, Zod 3 still runs the inner `.strict()` on
 *   `z.NEVER`'s `{ status: 'aborted' }` payload and emits a bogus
 *   `unrecognized_keys: ['status']` alongside the empty-patch message).
 *   Inputs with only unknown keys reach the inner schema and surface as
 *   `unrecognized_keys` without the secondary empty-patch message.
 * @aiSecurity none — purely a wire-contract precision helper.
 */
export function nonEmptyPatch<T extends z.ZodTypeAny>(inner: T): z.ZodEffects<T> {
  return z.preprocess((raw, ctx) => {
    if (
      typeof raw === 'object' &&
      raw !== null &&
      !Array.isArray(raw) &&
      Object.keys(raw as Record<string, unknown>).length === 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'patch must contain at least one field',
        fatal: true,
      });
      return z.NEVER;
    }
    return raw;
  }, inner) as unknown as z.ZodEffects<T>;
}
